// client-side js
// run by the browser each time your view template is loaded

import {
  makeCurve,
  makeGroup,
  makeHull,
  makePath,
  makePolyline,
  makeScene,
} from 'mint-turn';

import Vec2 from 'gl-vec2';
import Vec3 from 'gl-vec3';
import produce from 'immer';

import randomColor from 'randomcolor';

import makeTerrain from './terrain';
import makePlayer from './player';

import makeInput from './input';
import makeCamera from './camera';

import makePlatform from './platform';

const platforms = [
  makePlatform([-5, 2, 12.5], [3, 1, 2.5]),
  makePlatform([15, 10, 12], [2, 1, 2]),
  makePlatform([25, 15, 12], [2, 1, 2]),
  makePlatform([35, 20, 12], [3, 1, 2]),
  makePlatform([40, 25, 12], [2, 1, 2]),
  makePlatform([40, 30, 16], [2, 1, 2]),
  makePlatform([30, 35, 16], [2, 1, 2]),
  makePlatform([30, 40, 8], [2, 1, 2]),
  makePlatform([25, 45, 0], [3, 1, 3]),
];

const width = 12;
const height = 6;
const mapCoordinate = ([x, y, z]) => [x*3, Math.sin(x/3)*3+y+Math.sin(z/4)*3, z*3];
const terrainTriangles = (
  [...Array(width*2+1).keys()].map(i => (
    [...Array(height*2).keys()].map(j => {
      const left = i - width - 1;
      const spacing = Math.tan(Math.PI / 3);
      const bottom = (j - height) * spacing;
      if (i % 2 !== j % 2) {
        return [[left, 0, bottom], [left+1, 0, bottom+spacing], [left+2, 0, bottom]];
      }
      return [[left, 0, bottom+spacing], [left+2, 0, bottom+spacing], [left+1, 0, bottom]];
    })
  )).flat()
).map(triangle => triangle.map(mapCoordinate));
const [terrainGroups, getFloorIntersection, getWallIntersection] = makeTerrain([
  ...terrainTriangles,
  ...platforms.map(platform => platform[0]).flat(),
  // random platform
  [[3,4,3],[6,4,7],[5,4,1]],
  // back wall
  [[30,0,31],[-30,0,31],[0,20,31]],
  // cube platform
  [[2,8,10],[8,8,15],[8,8,10]],
  [[2,8,10],[2,8,15],[8,8,15]],
  [[8,3,10],[2,3,10],[8,8,10]],
  [[2,3,10],[2,8,10],[8,8,10]],
  [[8,3,15],[8,8,15],[2,3,15]],
  [[2,3,15],[8,8,15],[2,8,15]],
  [[2,3,15],[2,8,15],[2,3,10]],
  [[2,3,10],[2,8,15],[2,8,10]],
  [[8,3,15],[8,3,10],[8,8,15]],
  [[8,3,10],[8,8,10],[8,8,15]],
  // concave pyramid
  [[-20, 0, 10],[-20, 0, 5],[-20, 5, 10]],
  [[-20, 0, 10],[-20, 5, 10],[-15, 0, 10]],
  [[-25, 0, 10],[-20, 5, 10],[-20, 0, 5]],
  [[-20, 0, 15],[-15, 0, 10],[-20, 5, 10]],
  [[-20, 0, 15],[-20, 5, 10],[-25, 0, 10]],
  // corner elevator
  ...[...Array(100).keys()].map(i => [[-35, i*5, -30],[-35, i*5, -25],[-30, i*5, -30]]),
]);

const [player, updatePlayer, initialPlayerState, interpolatePlayer] = makePlayer(getFloorIntersection, getWallIntersection);

const groundCurveScale = 2;
const groundCurves = [...Array(20*groundCurveScale).keys()].map((i) => {
  const curveA = makeCurve([...Array(20*groundCurveScale).keys()].map((j) => mapCoordinate([i/groundCurveScale-10, 0, j/groundCurveScale-10])), false);
  curveA[0].setAttribute('fill', 'none');
  curveA[0].setAttribute('stroke', randomColor({ hue: 'D2B48C' }));
  curveA[0].setAttribute('stroke-width', 5);
  const curveB = makeCurve([...Array(20*groundCurveScale).keys()].map((j) => mapCoordinate([j/groundCurveScale-10, 0, i/groundCurveScale-10])), false);
  curveB[0].setAttribute('fill', 'none');
  curveB[0].setAttribute('stroke', randomColor({ hue: 'D2B48C' }));
  curveB[0].setAttribute('stroke-width', 5);
  return [curveA, curveB];
}).flat();

const cubeBlob = makeHull([[2,8,10],[8,8,10],[8,8,15],[2,8,15], [5,6,12.5]], 0.1);
cubeBlob[0].setAttribute('fill', 'green');
cubeBlob[0].setAttribute('stroke', 'none');

const [svg, camera, updateSvg] = makeScene(1920, 1080, [...groundCurves, ...platforms.map(platform => platform[1]), cubeBlob, player]);
svg.setAttribute('id', 'main');
document.body.appendChild(svg);

camera.position = [0, 5, -5];
camera.fov = Math.PI/2;

const getInput = makeInput();
const [updateCamera, initialCameraState, interpolateCamera] = makeCamera(camera);

let state = {
  time: 0,
  player: initialPlayerState,
  camera: initialCameraState,
};
let nextState = state;

const update = (state, dt) => {
  const input = getInput();
  const cameraDirection = Vec3.subtract([], state.camera.lookingAt, state.camera.position);
  const cameraRotation = -Math.atan2(cameraDirection[0], cameraDirection[2]);
  Vec2.rotate(input.movement, input.movement, cameraRotation);
  state.player = input.reset ? initialPlayerState : updatePlayer(state.player, dt, input);
  state.camera = updateCamera(state.camera, dt, input, state.player);
  state.time += dt;
  return state;
};

let accumulate = 0;
let lastTime = null;
const cycle = (currentTime) => {
  if (!lastTime) lastTime = currentTime;
  const timeSinceLastRender = (currentTime - lastTime) / 1000;
  lastTime = currentTime;
  
  if (timeSinceLastRender > 0.5) {
    // a lot of time passed, treat it as paused
    accumulate = 0;
  } else {
    accumulate += timeSinceLastRender;
  }
  
  const timePerUpdate = 1/240;
  while (accumulate > timePerUpdate) {
    state = nextState;
    nextState = produce(state, (draft) => update(draft, timePerUpdate));
    accumulate -= timePerUpdate;
  }
  
  const interpolate = accumulate / timePerUpdate;
  interpolateCamera(state.camera, nextState.camera, interpolate);
  interpolatePlayer(state.player, nextState.player, interpolate);
  
  updateSvg();
  window.requestAnimationFrame(cycle);
};
window.requestAnimationFrame(cycle);

console.log('hello world :o');